import type { AnchorHTMLAttributes, MouseEvent } from 'react';
import Swal from 'sweetalert2';

import { PASS_ORIGIN } from '@/lib/api/pass-origin';

const truncateMiddle = (fullStr?: string) => {
    if (!fullStr) {
        return '';
    }
    if (fullStr.length < 16) {
        return fullStr;
    }
    return fullStr.slice(0, 8) + '...' + fullStr.slice(-8);
};

export const downloadPassFile = async (href: string) => {
    const parsed = new URL(href);
    parsed.searchParams.delete('access_token');
    const url = parsed.toString();

    const anchor = document.createElement('a');
    document.body.appendChild(anchor);

    const urlSlices = /^.*\/(.*)\.([^?]*)\??.*$/.exec(url);
    // Comme avant : une URL sans extension fait échouer le téléchargement (urlSlices nul).
    const fileName = `${urlSlices![1]}.${urlSlices![2]}`;

    void Swal.fire({
        toast: true,
        position: 'top',
        showConfirmButton: false,
        didOpen: () => {
            Swal.showLoading();
        },
        titleText: `Téléchargement du fichier ${truncateMiddle(fileName)}...`,
    });

    const blob = await fetch(url, { credentials: 'include' }).then((response) => {
        if (response.ok) {
            return response.blob();
        }
        return undefined;
    });

    if (blob) {
        const objectUrl = window.URL.createObjectURL(blob);
        anchor.href = objectUrl;

        anchor.download = fileName;
        anchor.click();

        setTimeout(function () {
            document.body.removeChild(anchor);
            window.URL.revokeObjectURL(objectUrl);
        }, 100);

        // Ferme le toast de chargement. L'ancien code appelait `.close()` sur la promesse de
        // `Swal.fire` (inexistant) : le toast restait affiché et une TypeError était levée.
        Swal.close();
    } else {
        // Le toast d'échec remplace celui de chargement.
        void Swal.fire({
            toast: true,
            position: 'top',
            icon: 'error',
            title: 'Téléchargement échoué',
            confirmButtonText: "J'ai compris",
        });
    }
};

/** Hôtes du Pass, seuls destinataires d'un téléchargement avec les cookies de session. */
const PASS_HOSTS = [
    'pass.april-moto.com',
    'pass-recette.april-moto.com',
    'pass-live.april-moto.com',
    'pass.rec.dmz',
    // Pass de l'environnement courant (recette2, local…), lu dans la configuration.
    ...(PASS_ORIGIN ? [new URL(PASS_ORIGIN).hostname] : []),
];

/**
 * Comparaison sur le nom d'hôte, et non sur la chaîne de l'URL : un `indexOf` laissait passer
 * `https://pass.april-moto.com.example.org/…`, qui aurait reçu les cookies du Pass.
 */
const isPassHost = (hostname: string) => {
    const host = hostname.toLowerCase();
    return PASS_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
};

export interface APILinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
    href: string;
}

const APILink = (props: APILinkProps) => {
    const parsed = new URL(props.href);

    const downloadWithAuth = async (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        await downloadPassFile(props.href);
        if (props.onClick) props.onClick(e);
    };

    if (!isPassHost(parsed.hostname)) {
        // ce n'est pas un dl du pass, pas d'auth
        return (
            <a rel="noopener noreferrer" {...props}>
                {props.children}
            </a>
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- retirés du lien : le téléchargement est géré ici
    const { onClick, download, target, ...otherProps } = props;

    return (
        <a onClick={downloadWithAuth} rel="noopener noreferrer" {...otherProps}>
            {props.children}
        </a>
    );
};

export default APILink;
